import _isEmpty from 'lodash/isEmpty';
import Graphic from '@arcgis/core/Graphic';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import * as identify from '@arcgis/core/rest/identify';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';

// Controllers
import mapController from '../controllers/MapControllers';

// Store
import store from '../store/store';
import { setCountriesSurveySubLayers, setSelectedSurveySubLayers } from '../store/slices/mapSlice';

// Services
import { get_5x5_SurveySubLayers, getSelectedSurveySubLayers } from '../services/getSurveyRasterData.service';

// Configs
import { RASTER_FIELD_FOR_POPULATION } from '../configs/attributeFields.configs';
import {
  createRoundedValueExpInfo,
  POPUP_INDICATOR_LABEL_CLASS,
  POPUP_MARKER_COLOR,
  POPUP_MARKER_WIDTH,
  POPUP_REGION_NAME_CLASS,
  POPUP_VALUE_CLASS,
} from '../configs/popup.config';
import {
  ADMIN_2_REF_FEATURE_LAYER_ID,
  ADMIN__5X5km,
  RASTER_LAYER_VALUE_KEY,
  SIDEBAR_MAP_FILTER_FIELDS,
} from '../configs/map.configs';
import { SidebarMapFilterFieldsType } from '../configs/types';

// Adds a marker to the map at the location of the popup when the 5x5 layer is clicked
const addMarkerToPopupClick = (layer: __esri.GraphicsLayer, mapPoint: __esri.Point) => {
  const markerSymbol = {
    type: 'simple-marker',
    color: POPUP_MARKER_COLOR,
    outline: {
      color: POPUP_MARKER_COLOR,
      width: 0,
    },
    size: POPUP_MARKER_WIDTH,
  };

  const graphic = new Graphic({
    geometry: mapPoint,
    symbol: markerSymbol,
  });

  layer.graphics.removeAll();
  layer.add(graphic);
};

/**
 * Get the label for the currently selected field in the sidebar
 * @returns {string} - selected field label
 */
export const getCurrentFieldSelectedLabel = (): string => {
  const fieldSelected = store.getState().sidebarMapFiltersSlice.fieldSelected;
  const fieldSelectedItem = SIDEBAR_MAP_FILTER_FIELDS.find((item: SidebarMapFilterFieldsType) => {
    return item.value === fieldSelected;
  });
  const fieldSelectedItemLabel = fieldSelectedItem?.label;
  return fieldSelectedItemLabel || 'FIELD NOT FOUND';
};

/**
 * Get the region name formatted for the popup
 * @param {string} admin1Name - The name of the admin 1 region
 * @param {string} admin2Name - The name of the admin 2 region
 * @returns {string} - The formatted region names
 */
export function getRegionNameFormatted(admin1Name: string, admin2Name?: string): string {
  if (!admin2Name) {
    return `${admin1Name} (L1)`;
  } else {
    return `${admin1Name} (L1) > ${admin2Name} (L2)`;
  }
}

/**
 * Create and open popup for 5 x 5 km layer
 * @param {__esri.Point} mapPoint - Point where the popup should open
 * @param {Object} admin2Result - Result of the admin 2 layer hit test
 * @param {Object} selectedSurveySubLayersWithValues - The selected survey sublayers with their values
 */
const createAndOpenPopupFor5x5 = (
  mapPoint: __esri.Point,
  admin2Result: any,
  selectedSurveySubLayersWithValues: any
): void => {
  const selectedIndicatorLabel = store.getState().admin2EstimatesSlice.indicatorSelected?.label;
  const currentFieldSelectedLabel = getCurrentFieldSelectedLabel();

  const dataValues = SIDEBAR_MAP_FILTER_FIELDS.map((item: SidebarMapFilterFieldsType) => {
    const subLayer = selectedSurveySubLayersWithValues[item.raster_type];

    let value;
    if (item.raster_type === RASTER_FIELD_FOR_POPULATION) {
      value = Math.floor(parseFloat(subLayer?.value));
    } else {
      value = parseFloat(subLayer?.value) * item.raster_type_multiplier;
    }

    const valueFormatted = item.format(value);
    const labelClass = currentFieldSelectedLabel === item.label ? 'strong' : '';
    return `<p class='${POPUP_VALUE_CLASS}'><span class='${labelClass}'>${item.label}</span>: ${valueFormatted}</p>`;
  });

  const admin1Name: string = admin2Result?.graphic?.attributes?.ADM1_NAME;
  const admin2Name: string = admin2Result?.graphic?.attributes?.ADM2_NAME;
  const regionNameFormatted = getRegionNameFormatted(admin1Name, admin2Name);

  const content =
    `<p class='${POPUP_INDICATOR_LABEL_CLASS}'>${selectedIndicatorLabel}</p>` +
    `<p class='${POPUP_REGION_NAME_CLASS}'>${regionNameFormatted}</p>` +
    `<div class='popup-content__5x5'>${dataValues.join('')}</div>`;

  const title: string | undefined = store.getState().admin2EstimatesSlice.surveySelected?.value;
  if (mapController.mapView) {
    mapController.mapView.openPopup({
      location: mapPoint,
      title,
      content,
    });
  }
};

/**
 * Filter the selected survey sublayers
 * @param {__esri.MapImageLayer} layer - The map image layer
 * @returns {[]} - The selected survey sublayers
 */
const filterSelectedSurveySubLayers = async (layer: __esri.MapImageLayer) => {
  const { surveySelected } = store.getState().admin2EstimatesSlice;
  const { countriesSurveySubLayers: countriesSurveySubLayersAll } = store.getState().mapSlice;
  const countrySurveysRaster = `Admin2SDR_rasters_${surveySelected?.DHSCC}`;
  const countySurveySubLayer = countriesSurveySubLayersAll[countrySurveysRaster];

  if (countySurveySubLayer) {
    const selectedSurveySubLayers = getSelectedSurveySubLayers(countySurveySubLayer);
    store.dispatch(setSelectedSurveySubLayers(selectedSurveySubLayers));

    return selectedSurveySubLayers;
  } else {
    const { countriesSurveySubLayers, selectedSurveySubLayers } = await get_5x5_SurveySubLayers(layer);
    store.dispatch(setSelectedSurveySubLayers(selectedSurveySubLayers));
    store.dispatch(setCountriesSurveySubLayers({ ...countriesSurveySubLayersAll, ...countriesSurveySubLayers }));

    return selectedSurveySubLayers;
  }
};

async function identifySubLayerValue(
  mapPoint: __esri.Point,
  subLayer: __esri.Sublayer,
  mapView: __esri.MapView,
  layer: __esri.MapImageLayer
) {
  const params = new IdentifyParameters();
  params.geometry = mapPoint;
  params.tolerance = 3;
  params.layerIds = [subLayer.id];
  params.width = mapView.width;
  params.height = mapView.height;
  params.mapExtent = mapView.extent;

  const { results } = await identify.identify(layer.url, params).catch((error) => {
    console.error(error);
  });

  return results;
}

export const handleMapImageLayerPopup = (
  event: __esri.ViewClickEvent,
  mapView: __esri.MapView,
  layer: __esri.MapImageLayer
) => {
  const { mapPoint } = event;

  // Get Admin 1 and 2 names for popup
  if (layer && mapPoint) {
    mapView.hitTest(event).then(async (response) => {
      const results = response.results;
      if (results.length > 0) {
        const admin2Result = results.find((result) => result.layer.id === ADMIN_2_REF_FEATURE_LAYER_ID);
        if (admin2Result) {
          const adminLevel = store.getState().sidebarMapFiltersSlice.adminSelected;
          const activeSubLayer = layer.allSublayers.find((sublayer: __esri.Sublayer) => sublayer.visible);

          if (adminLevel === ADMIN__5X5km && activeSubLayer) {
            const selectedSurveySubLayers = await filterSelectedSurveySubLayers(layer);

            const selectedSurveySubLayersWithValues: any = {};
            for (const subLayer of selectedSurveySubLayers) {
              // Identify the pixel value of the raster layer
              const response = await identifySubLayerValue(mapPoint, subLayer, mapView, layer);
              const { attributes } = response[0].feature;

              selectedSurveySubLayersWithValues[subLayer.raster_type] = {
                ...subLayer,
                value: attributes[RASTER_LAYER_VALUE_KEY],
              };
            }

            if (!_isEmpty(selectedSurveySubLayersWithValues)) {
              createAndOpenPopupFor5x5(mapPoint, admin2Result, selectedSurveySubLayersWithValues);

              // Add marker to map
              if (mapController.popupMarkerGraphicsLayer) {
                addMarkerToPopupClick(mapController.popupMarkerGraphicsLayer, mapPoint);
              }
            }
          }
        }
      }
    });
  }
};

// The field in the expression can change depending on the field selected in the sidebar
// This function updates the expression to reflect the currently selected field
export const updatePopupTemplateForFieldSelected = (layer: __esri.FeatureLayer | __esri.Sublayer): any => {
  layer.popupTemplate.expressionInfos = [createRoundedValueExpInfo()] as any;
};

export const setupPopupCloseHandler = (mapView: __esri.MapView, layer: __esri.GraphicsLayer) => {
  reactiveUtils.when(
    () => !mapView.popup.visible,
    () => {
      if (layer) {
        layer.graphics.removeAll();
      }
    }
  );
};
